/**
 * The energy die, tap-to-edit: opens the same six-way `EnergyPicker` the full editor uses
 * (unset, then 1–5), closing on a pick, an outside pointerdown, or Escape — the same
 * open/close contract `BadgeGlossary`'s popover already uses, so the app has one popover
 * behaviour rather than several slightly different ones.
 *
 * Asked for directly: setting a task or subtask's own energy without opening the full editor
 * for one field. Shared by `TaskList.tsx` (top-level rows) and `Subtasks.tsx` (subtask rows,
 * where the picked value is also expected to be *lower* than the parent's — what makes a
 * broken-down task reachable on a low-energy day, §8.6.4) — the control itself doesn't care
 * which context it's in, only `onChange` does.
 *
 * Purely presentational: it neither knows nor calls any API — persistence is entirely the
 * caller's job via `onChange`, same as every other row action funnelling through
 * `TaskActions.onEdit`.
 */

import { useEffect, useRef, useState } from "react";
import type { Task } from "../api";
import { EnergyPicker } from "./TaskFields";
import { Die } from "./Die";
import { energyTitle } from "./taskFormat";

export function EnergyBadgeButton({
  task,
  onChange,
}: {
  task: Task;
  onChange: (value: number | null) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent) {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <span className="energy-badge-button" ref={rootRef}>
      <button
        type="button"
        className="badge energy tappable"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={`Change energy: ${task.title}, currently ${energyTitle(task.energy)}`}
        title={`${energyTitle(task.energy)} - tap to change`}
      >
        <Die value={task.energy} />
      </button>
      {open && (
        <span className="energy-popover" role="dialog" aria-label="Set energy">
          <EnergyPicker
            value={task.energy}
            onChange={(value) => {
              setOpen(false);
              void onChange(value);
            }}
          />
        </span>
      )}
    </span>
  );
}
